import { mapPayerNameToCodeForDropdown } from "constants/practiceType";
import {
  IPracticeTypeSwitch,
  ProcedureModality,
} from "types/PracticeTypeSuggestion/PracticeTypeSuggestion";

/**
 * CLE-2971 — the words for an automatic Service Type switch. Shared by the notice beside the Service Type
 * dropdown, the toast `EligibilityButton` shows when a run in this page session switches, and the toast
 * the page trigger shows the moment it asks for the re-run.
 */
const MODALITY_WORDING: Record<IPracticeTypeSwitch["modality"], string> = {
  MRI: "an MRI",
  CT: "a CT scan",
  ULTRASOUND: "an ultrasound",
  // CLE-3000 — the new CPT groups.
  NUCLEAR_MEDICINE: "a nuclear medicine study",
  SLEEP_STUDY: "a sleep study",
  OTHER_DIAGNOSTIC_TEST: "a diagnostic test",
  XRAY: "an X-ray",
};

/** The Service Type dropdown's own label for a code; the bare code when the dropdown does not offer it. */
export const practiceTypeName = (code: string): string =>
  mapPayerNameToCodeForDropdown.find((option) => option.value === code)?.name ??
  `Service Type ${code}`;

export const buildPracticeTypeSwitchMessage = (
  practiceTypeSwitch: IPracticeTypeSwitch
): string =>
  `Service Type changed from ${practiceTypeName(
    practiceTypeSwitch.from
  )} to ${practiceTypeName(practiceTypeSwitch.to)} because this order has ${
    MODALITY_WORDING[practiceTypeSwitch.modality] ?? "a procedure"
  }.`;

/**
 * The page trigger's toast, shown the moment it asks for a re-run — the pVerify answer can take 30
 * seconds, and until then the estimate cannot be created. `from` is the code the last check ran under,
 * `to` the rule's target. A modality with no wording (OTHER) falls back to the procedures in general.
 */
export const buildPracticeTypeRerunMessage = ({
  from,
  to,
  modality,
}: {
  from: string;
  to: string;
  modality?: ProcedureModality | null;
}): string => {
  const wording = modality
    ? (MODALITY_WORDING as Record<string, string>)[modality]
    : undefined;
  const change = `its Service Type is changing from ${practiceTypeName(
    from
  )} to ${practiceTypeName(to)}. Re-running eligibility…`;
  return wording
    ? `This estimate has ${wording}, so ${change}`
    : `Because of this estimate's procedures, ${change}`;
};
